<template>
    <div class="flex flex-column p-4">
        <ConfirmDialog />
        <div class="flex  flex-wrap justify-content-between gap-2 py-2">
            <div class="flex justify-content-start align-items-center gap-2 mb-3">
                <img :src="profileImage" class="profile-image" alt="Image" />
                <div class="flex flex-column justify-content-start gap-2 pb-2 pl-2">
                    <span class="text-left Paragraph-Medium">{{ profile ? profile.username : 'loading...' }}</span>
                    <span class="Caption">{{ tutorClassTypeText }}</span>
                </div>
            </div>
            <div class="flex align-items-center gap-2">
                <ChatButton v-if="profile" :otherUserId="tutorId" :otherUserName="profile.username"
                    @open-chat="$emit('open-chat', $event)" />
                <Button label="View Profile" class="orange-button-small" @click="navigateToProfile" />
                <Menu :model="menuItems" :popup="true" ref="menu" class="menu-item" />
                <Button class="menu-button" @click="toggleMenu" aria-haspopup="true" aria-controls="overlay_menu">
                    <svg width="4" height="16" viewBox="0 0 4 16" fill="none">
                        <circle cx="2" cy="2" r="2" fill="currentColor" />
                        <circle cx="2" cy="8" r="2" fill="currentColor" />
                        <circle cx="2" cy="14" r="2" fill="currentColor" />
                    </svg>
                </Button>
            </div>
            <Divider class="w-full" />
        </div>
        <div v-if="currentView === 'main'">
            <div class="flex flex-wrap justify-content-end gap-2 py-4">
                <Button label="Previous classes" class="orange-button-transparent tutor-profile-btn" @click="showPreviousClassesView" />
                <Button label="Request New Class" class="orange-button tutor-profile-btn" @click="showRequestClassView" />
            </div>
            <div class="flex flex-column">
                <span class="Paragraph-Medium">Subjects</span>
                <div class="flex flex-wrap align-items-start justify-content-start gap-3 py-5 ">
                    <span v-for="module in subjects" :key="module"
                        class="flex items-center justify-center px-2 py-2 rounded Caption subjects">
                        {{ cleanupLables(module) }}</span>
                </div>
            </div>
            <div class="flex flex-column">
                <span class="Paragraph-Medium mb-4">Upcoming classes</span>
                <p style="margin-bottom: 10px; margin-top: -10px; font-size: 13px;">💡 <b>Reminder:</b> To ensure uninterrupted access, please complete your payment before the scheduled class time.</p>
                <!-- Upcoming classes -->
                <div class="grid-container">
                    <div v-for="(item, index) in upcomingClasses" :key="index">
                        <Card :class="['Card p-4 h-full', { 'cancelled-card': isCancelledClass(item) }]">
                            <template #content>
                                <div class="class-card-header">
                                    <Button v-if="canCancelClass(item)" icon="pi pi-times-circle" class="p-button-text p-button-sm p-0 cancel-class-btn" title="Cancel Class" @click.stop="confirmCancelClass(item.id)" />
                                    <span v-else class="cancel-icon-placeholder"></span>
                                    <Badge v-if="isCancelledClass(item)" severity="danger">Cancelled</Badge>
                                    <Badge v-else :severity="item.isPaid ? 'success' : 'warn'">{{ item.isPaid ? 'Paid' : 'Payment Pending' }}</Badge>
                                </div>
                                <div class="flex flex-column gap-2 w-full">
                                    <span
                                        class="Paragraph-Medium mb-2 white-space-nowrap overflow-hidden text-overflow-ellipsis"
                                        :title="item.subject">
                                        {{ cleanupLables(item.subject) }}
                                    </span>
                                    <span class="Caption">{{ cleanupLables(item.classType) }}</span>
                                    <span class="Caption" v-if="item.location">At {{ cleanupLables(item.location)
                                    }}</span>
                                    <span class="Caption flex gap-3">
                                        <span>{{ item.classDate }}</span>
                                        <span>{{ item.classTime }}</span>
                                    </span>
                                </div>
                            </template>
                            <template #footer>
                                <div class="flex justify-content-center mt-3">
                                    <Button label="View" class="orange-button-small view w-8rem"
                                        :disabled="isCancelledClass(item)"
                                        @click="showClassDetailsView(item.id)" />
                                </div>
                            </template>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
        <div v-else-if="currentView === 'previousClasses'">
            <PreviousClasses :previousClasses="previousClasses" @back="showMainView"
                @view-class-details="showClassDetailsView" />
        </div>
        <div v-else-if="currentView === 'requestClass'">
            <RequestClass :tutor="tutorId" :innerUseage="true" @back="showMainView" />
        </div>
        <div v-else-if="currentView === 'classDetails'">
            <ClassInstanceDetails :classInstanceId="selectedClassInstanceId" @back="showMainView" @payment-completed="markClassAsPaid" />
        </div>
    </div>

</template>

<script setup>
import Card from "primevue/card";
import Button from "primevue/button";
import Divider from "primevue/divider";
import { defineProps, onMounted, ref, computed, defineEmits, watchEffect } from 'vue';
import router from "@/router";
import { fetchClassesByTutor, fetchPreviousClassesByTutor, endAllClassesByTutor, cancelClassInstance } from '@/api/student';
import { publicProfile } from "@/api/tutor";
import Menu from 'primevue/menu';
import { useConfirm } from 'primevue/useconfirm';
import ConfirmDialog from 'primevue/confirmdialog';
import { notifySuccess, notifyError } from '@/utils/notification';
import PreviousClasses from './previousClasses.vue';
import ClassInstanceDetails from './classInstanceDetails.vue';
import RequestClass from '@/views/student/RequestClass.vue';
import blankProfile from '@/assets/blank-profile.png';
import { generateSignedUrl } from "@/api/storage";
import { cleanupLables } from "@/utils/cleanups";
import ChatButton from "@/components/ChatButton.vue";
import { Badge } from "primevue";

const confirm = useConfirm();

const emit = defineEmits(['classes-ended', 'open-chat']);

const props = defineProps({
    tutorId: {
        type: String,
        required: true
    }
});

const currentView = ref('main');
const upcomingClasses = ref([]);
const subjects = ref([]);
const previousClasses = ref([]);
const profile = ref(null);
const selectedClassInstanceId = ref(null);
const menu = ref();
const menuItems = ref([
    {
        label: 'End classes (Unenroll)',
        command: () => {
            endAllClasses();
        }
    }
]);
const navigateToProfile = () => {
    router.push(`/tutorProfile/${props.tutorId}`);
}

const showPreviousClassesView = () => {
    currentView.value = 'previousClasses';
};

const showMainView = () => {
    currentView.value = 'main';
};

const showRequestClassView = () => {
    currentView.value = 'requestClass';
};

const showClassDetailsView = (classInstanceId) => {
    selectedClassInstanceId.value = classInstanceId;
    currentView.value = 'classDetails';
};

const markClassAsPaid = (classInstanceId) => {
    const cls = upcomingClasses.value.find(c => c.id === classInstanceId);
    if (cls) cls.isPaid = true;
};

const isCancelledClass = (item) => {
    const status = (item?.classStatus ?? item?.status ?? '').toString().toLowerCase();
    return status === 'cancelled' || status === 'canceled';
};

const isUpcomingClass = (item) => {
    const status = (item?.classStatus ?? item?.status ?? '').toString().toLowerCase();
    return status === 'upcoming';
};

const canCancelClass = (item) => !isCancelledClass(item) && isUpcomingClass(item);

const confirmCancelClass = (classInstanceId) => {
    confirm.require({
        message: 'Are you sure you want to cancel this pending class? This action cannot be undone.',
        header: 'Cancel Class',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Yes, Cancel Class',
        rejectLabel: 'Keep Class',
        acceptClass: 'p-button-danger',
        rejectClass: 'p-button-text p-button-secondary',
        accept: async () => {
            try {
                await cancelClassInstance(classInstanceId, { refundNote: '' });
                notifySuccess('Success', 'This class has been cancelled.');
                await fetchClasses();
            } catch (error) {
                notifyError('Error', error?.response?.data?.message || 'Failed to cancel class. Please try again.');
            }
        },
    });
};

const profileImage = ref(blankProfile);

watchEffect(async () => {
    if (profile.value?.professionalPhotoURL) {
        profileImage.value = await generateSignedUrl(profile.value.professionalPhotoURL);
    } else {
        profileImage.value = blankProfile;
    }
});

const tutorClassTypeText = computed(() => {
    if (!profile.value) return "loading...";

    const levels = profile.value.teachingLevels ?? [];
    const individual = levels.some(l => l.individualClass?.available);
    const group = levels.some(l => l.groupClass?.available);

    if (individual && group) return "Individual & Group Classes";
    if (individual) return "Individual Classes";
    if (group) return "Group Classes";

    return "Tutor Class Types Unavailable";
});

const fetchClasses = async () => {
    subjects.value = [];
    upcomingClasses.value = [];

    const res = await fetchClassesByTutor(props.tutorId);
    if (res.data && res.data.data) {
        res.data.data.forEach((item) => {
            if (!subjects.value.includes(item.subject)) {
                subjects.value.push(item.subject);
            }
            upcomingClasses.value.push({
                id: item.classInstanceId,
                subject: item.subject,
                classType: item.classType,
                location: item.classLocation ? item.classLocation : "",
                classDate: item.date,
                classTime: `${item.start} - ${item.end}`,
                isPaid: item.isPaid ?? false,
                classStatus: item.classStatus ?? item.status ?? '',
            })
        });
    }
}

const fetchPreviousClasses = async () => {
    previousClasses.value = [];

    const res = await fetchPreviousClassesByTutor(props.tutorId);
    if (res.data && res.data.data) {
        res.data.data.forEach((item) => {
            if (!subjects.value.includes(item.subject)) {
                subjects.value.push(item.subject);
            }
            previousClasses.value.push({
                id: item.classInstanceId,
                subject: item.subject,
                classType: item.classType,
                location: item.classLocation ? item.classLocation : "",
                classDate: item.date,
                classTime: `${item.start} - ${item.end}`,
                isPaid: item.isPaid ?? false,
            })
        });
    }
}

const fetchTutorProfile = async () => {
    const prof = await publicProfile(props.tutorId);
    profile.value = prof.data.data;
}

const toggleMenu = (event) => {
    menu.value.toggle(event);
};

const endAllClasses = () => {
    confirm.require({
        message: 'Are you sure you want to end all classes with this tutor? This action cannot be undone.',
        header: 'End All Classes',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Yes, End All Classes',
        rejectLabel: 'Cancel',
        acceptClass: 'orange-button',
        rejectClass: 'orange-button-transparent',
        accept: async () => {
            try {
                await endAllClassesByTutor(props.tutorId);
                notifySuccess('Success', 'All classes with this tutor have been ended.');
                emit('classes-ended');
            } catch (error) {
                notifyError('Error', 'Failed to end classes. Please try again.');
            }
        },
    });
};

onMounted(() => {
    fetchClasses();
    fetchPreviousClasses();
    fetchTutorProfile();
});
</script>
<style scoped>
.Card {
    /* background-color: var(--teal-800); */
    border-radius: 4px;
    box-shadow: 0 0 4px 1px rgba(0, 0, 0, 0.16);
}

.view {
    padding: 8px 16px 8px 16px;
    height: 30px;
    font-size: 0.75rem;

}

.p-button-danger {
    padding: 10px 16px 10px 16px;
    height: 30px;
}

.text-gray-700 {
    color: var(--gray-700) !important;
    padding-top: 30vh;
}



.menu-button {
    background: none;
    border: none;
    padding: 8px 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--gray-700) !important;
}

.menu-button:hover {
    background: transparent !important;
    border: none;
    padding: 8px 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--gray-700) !important;
}


.menu-item {
    padding: 8px 12px;
    cursor: pointer;
    border-bottom: 1px solid #f0f0f0;
    color: var(--orange-500) !important;
}

.menu-item:hover {
    background-color: #f5f5f5;
}

.menu-item:last-child {
    border-bottom: none;
}

.menu-container:hover .menu-dropdown {
    display: block;
}

.grid-container {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr));
    gap: 3rem
}

.orange-button-small {
    width: 8rem !important;
}

.Card {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
}

.subjects {
    border: 1px solid var(--gray-700) !important;
    border-radius: 50px;
    background-color: transparent;
}

.class-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1rem;
}

.cancel-icon-placeholder {
    width: 1.5rem;
    display: inline-block;
}

.cancel-class-btn {
    color: var(--red-500) !important;
    justify-content: flex-start !important;
}

.view:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.cancelled-card {
    opacity: 0.72;
    background: #fff7f7;
    border: 1px solid rgba(220, 38, 38, 0.25);
}

@media (max-width: 600px) {
    .tutor-profile-btn {
        width: 100%;
        justify-content: center;
        white-space: nowrap;
    }
}
</style>